import CheckIcon from "@mui/icons-material/Check";
import ContrastIcon from "@mui/icons-material/Contrast";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { Collapse, ListItemIcon, ListItemText, type SvgIconProps } from "@mui/material";
import { useColorScheme } from "@mui/material/styles";
import { AnimatePresence, domAnimation, LazyMotion } from "framer-motion";
import type { FC } from "react";
import { useState } from "react";
import { AnimatedCheckmarkContainer, AnimatedIconContainer } from "./AnimatedContainers";
import {
  ExpandIcon,
  ThemeAccordionTrigger,
  ThemeOptionItem,
  ThemeOptionsContainer,
} from "./themeSelector.styles";

export type ThemeMode = "light" | "dark" | "system";

const THEME_OPTIONS: { mode: ThemeMode; Icon: FC<SvgIconProps>; label: string }[] = [
  { mode: "light", Icon: LightModeOutlinedIcon, label: "Light" },
  { mode: "dark", Icon: DarkModeOutlinedIcon, label: "Dark" },
  { mode: "system", Icon: ContrastIcon, label: "System" },
];

const AnimatedCheckmark = ({ isVisible }: { isVisible: boolean }) => (
  <AnimatePresence>
    {isVisible && (
      <AnimatedCheckmarkContainer>
        <CheckIcon fontSize="small" color="primary" />
      </AnimatedCheckmarkContainer>
    )}
  </AnimatePresence>
);

const ThemeSelector = () => {
  const { mode, setMode, systemMode } = useColorScheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const isDark = mode === "dark" || (mode === "system" && systemMode === "dark");

  const handleSelect = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  return (
    <LazyMotion features={domAnimation}>
      <ThemeAccordionTrigger
        onClick={() => setIsExpanded(!isExpanded)}
        data-testid="theme-selector"
        aria-expanded={isExpanded}
      >
        <ListItemIcon>
          <AnimatePresence mode="wait">
            <AnimatedIconContainer key={isDark ? "dark" : "light"}>
              {isDark ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
            </AnimatedIconContainer>
          </AnimatePresence>
        </ListItemIcon>
        <ListItemText primary="App Theme" secondary={mode} />
        <ExpandIcon isExpanded={isExpanded} fontSize="small" />
      </ThemeAccordionTrigger>

      <Collapse in={isExpanded}>
        <ThemeOptionsContainer>
          {THEME_OPTIONS.map(({ mode: optionMode, Icon, label }) => (
            <ThemeOptionItem
              key={optionMode}
              onClick={() => handleSelect(optionMode)}
              selected={mode === optionMode}
              data-testid={`theme-option-${optionMode}`}
            >
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText>{label}</ListItemText>
              <AnimatedCheckmark isVisible={mode === optionMode} />
            </ThemeOptionItem>
          ))}
        </ThemeOptionsContainer>
      </Collapse>
    </LazyMotion>
  );
};

export default ThemeSelector;
